import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {AppState} from '../../../store/appState';
import {
  getLegacyApplications,
  getLegacySidePanelEntryPoints,
} from '../../../store/data/applications/legacyApplicationsSelectors';
import {getIntegrations} from '../../../store/data/dataSelectors';
import {updateSelectedSideFrameIdAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {pinFrameAction} from '../../../store/data/preferences/preferencesActions';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {makeGetAppPanel} from '../../../store/panels/app/appPanelSelectors';
import {ApplicationBannerState} from '../../../store/panels/app/banners/bannersState';
import {AppContentTypesEnum} from '../../../store/panels/app/parameters/appParameters';
import {AppPanelId} from '../../../store/panels/panelStateBase';
import {getSortedVisiblePanelFrames} from '../../../store/stateless/sidePanelFrameSelectors';
import {ApplicationBanner} from './applicationBanner';

/*
 * Props.
 */

interface ApplicationBannerContainerProps {
  bannerState: ApplicationBannerState;
  panelId: AppPanelId;
}

function getState(_state: AppState, props: ApplicationBannerContainerProps) {
  return props.bannerState;
}

const getIntegration = createSelector([getIntegrations, getState], (integrations, state) => {
  if (!state.integrationId) {
    return undefined;
  }
  return integrations[state.integrationId];
});

const getApplication = createSelector([getLegacyApplications, getState], (applications, state) => {
  if (!state.legacyApplicationId) {
    return undefined;
  }
  return applications[state.legacyApplicationId];
});

const getFrameId = createSelector(
  [getLegacySidePanelEntryPoints, getIntegration, getApplication],
  (entryPoints, integration, application) => {
    if (!integration && !application) {
      return undefined;
    }
    // If the integration has a plugin natively we can just set this as the frame id to render.
    if (integration && integration.hasPlugin) {
      return integration.frameId;
    }

    // Check for entrypoints for plugins.
    const entryPoint = application && entryPoints.find((e) => e.applicationId === application.id);
    if (entryPoint) {
      return entryPoint.frameId;
    }

    return undefined;
  },
);

const getIsInInboxesTab = createSelector(
  makeGetAppPanel(),
  (panel) => panel.contentType === AppContentTypesEnum.INBOXES,
);

const getIsPinned = createSelector([getFrameId, getSortedVisiblePanelFrames], (frameId, pinnedFrames) =>
  pinnedFrames.some((frame) => frame.frameId === frameId),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ApplicationBannerContainerProps>()(() => ({
  frameId: getFrameId,
  application: getApplication,
  integration: getIntegration,
  user: getUser,
  isInInboxesTab: getIsInInboxesTab,
  isPinned: getIsPinned,
}));

const mapDispatchToProps = makeMapDispatchToProps<ApplicationBannerContainerProps>()((props) => ({
  openSideBar: updateSelectedSideFrameIdAction,
  pinPlugin: pinFrameAction,
}));

export const ApplicationBannerContainer = connect(mapStateToProps, mapDispatchToProps)(ApplicationBanner);
